/**
 * FTE-rekenlaag — pure functies, geen UI.
 *
 * Zet de server-momentopname (GET /api/rooster, zie server/src/rooster-service.js) om naar
 * een FTE-overzicht per gemeente per week. Bron is bewust de respons-weken (dagen -> diensten
 * met gemeenteKey + namen + afwezigen) en NIET de lokale maandrooster.weken: goedgekeurde ruils
 * zitten alleen in de server-momentopname verwerkt.
 *
 * Twee soorten uren, twee bronnen:
 *   - Straatdiensten splitsen vanzelf per gemeente — elke dienst in de respons draagt zijn
 *     eigen gemeenteKey, dus mw.gemeenten speelt hier geen rol.
 *   - Kantoor- en schooluren zijn NIET gemeente-gebonden: een kantoor-/schooldag is werk voor
 *     de hele omgeving. Die uren worden verdeeld over de ACTIEVE GEMEENTEN VAN DEZE LADE, dus
 *     gedeeld door actieveGemeenten.length. Stond eerder op een hardgecodeerde 4 (het aantal
 *     gemeenten over alle lades samen), waardoor de som over deze lade bewust 3/4 van de echte
 *     uren was — Maastricht kreeg een kwart toebedeeld terwijl het een andere omgeving met een
 *     eigen budget is. Nu telt alleen wat in deze lade staat, en klopt de som weer op 100%.
 *
 * WAT TELT ALS GELEVERD. Alleen straatdiensten. Kantoor en school zijn allebei INEFFECTIEF:
 * het zijn wel gewerkte uren, maar ze komen niet op straat terecht, en dat is de vraag die dit
 * overzicht beantwoordt. Kantoor telde eerder als geleverd (het zat in de fte-teller); dat is
 * bewust omgezet. School was al ineffectief en blijft dat, ook als de persoon er gewoon was.
 */
import { isRoosterbaar } from './rollen.js';
import { schoolTeltAlsWerk, schoolweekType, wouldBeWerkdag } from '../engine/rooster-engine.js';
import { bouwAfwezigheidMap, bouwAfwezigheidTypeMap, ALLE_DAGEN } from '../data/afwezigheid.js';
import { vakantieDagenInWeek } from '../data/vakanties.js';

/**
 * Bereken FTE per gemeente per week.
 *
 * @param {object|Array} roosterRespons  GET /api/rooster-respons ({ weken: [...] }) of direct de weken-array.
 * @param {object}       regels           regels.gemeenten (voor de actieve gemeenten) + regels.medewerkers.
 * @param {Array}        afwezigheid      afwezigheid-lijst, voor de kantoordag-afwezig-check.
 * @param {Array}        vakanties        schoolvakantie-periodes; in een vakantieweek vervalt de schooldag
 *                                        (geen schooluren, en de dag telt wél als werkdag). Leeg = geen vakanties.
 * @returns {Array<{ weeknummer: number, isoJaar: number, gemeenten: Object<string, {
 *   ingepland: number, ineffectief: number, effectief: number,
 *   kantoorUren: number, schoolUren: number, kantoorIneffUren: number, schoolIneffUren: number,
 *   ineffectiefUren: number, fte: number,
 *   afwezigen: Object<string, { type: string, aantal: number }> }>,
 *   team: { aantal: number, capaciteitUren: number, nietInzetbaarUren: number,
 *   nietIngeplandUren: number, uitgevallenUren: number, inzetbaarUren: number,
 *   afwezigen: Object<string, { type: string, typen: string[], uren: number,
 *     urenUitgevallen: number, typenUitgevallen: string[],
 *     urenNietIngepland: number, typenNietIngepland: string[] }> } }>}
 */
export function berekenFte(roosterRespons, regels, afwezigheid, vakanties = []) {
  const weken = Array.isArray(roosterRespons) ? roosterRespons : (roosterRespons?.weken || []);

  // Actieve gemeenten van deze lade (skip meta-sleutels en actief: false).
  const actieveGemeenten = Object.keys(regels?.gemeenten || {})
    .filter((k) => !k.startsWith('_') && regels.gemeenten[k].actief !== false);

  // Deler voor de kantoor/school-uren: het aantal gemeenten waarover ze verdeeld worden.
  // `|| 1` is puur een deel-door-nul-slot: zonder actieve gemeenten is er geen enkele
  // gemeente-rij en wordt de uitkomst nergens gelezen. Terugvallen op 1 houdt het getal
  // eindig; 0 zou Infinity opleveren en dat lekt zichtbaar weg zodra er later tóch iets
  // mee gerekend wordt.
  const deel = actieveGemeenten.length || 1;

  // Roosterbare medewerkers (skip meta-sleutels + teamleider-only). Kantoor/school van elk van
  // hen telt mee, ongeacht mw.gemeenten (die uren zijn niet gemeente-gebonden).
  const roosterbareMw = Object.entries(regels?.medewerkers || {})
    .filter(([k, mw]) => !k.startsWith('_') && isRoosterbaar(mw))
    .map(([, mw]) => mw);

  return weken.map((week) => {
    const weekType = schoolweekType(week.weeknummer);
    // naam -> Set(dagcodes) voor DEZE week; weeknummer + isoJaar bepalen welke dagen een
    // datumbereik-afwezigheid raakt (bouwAfwezigheidMap per week aanroepen).
    const afwMap = bouwAfwezigheidMap(afwezigheid, week.weeknummer, week.isoJaar);
    // Vakantiedagen van DEZE week (Set<dag-code>), zelfde bouw als in de engine en de matrix:
    // valt de schooldag in een schoolvakantie, dan vervalt hij helemaal — geen schooluren én
    // de dag telt mee als gewone werkdag in de teamcapaciteit hieronder.
    const vakantieDagen = new Set(vakantieDagenInWeek(week.weeknummer, week.isoJaar, vakanties));

    // ── Kantoor/school: identiek voor elke actieve gemeente (uren / deel per gemeente) ──
    let kantoorUren = 0;      // effectieve kantooruren (per gemeente)
    let schoolUren = 0;       // schooluren (per gemeente) — altijd ineffectief
    let kantoorIneffUren = 0; // kantooruren op een afwezig-dag (per gemeente) — ineffectief
    let schoolIneffUren = 0;  // schooluren op een afwezig-dag (per gemeente) — ineffectief
    for (const mw of roosterbareMw) {
      for (const dag of mw.kantoor_dagen || []) {
        const uren = 8 / deel;
        if (afwMap[mw.naam]?.has(dag)) kantoorIneffUren += uren; // afwezig die dag -> ineffectief i.p.v.
        else kantoorUren += uren;
      }
      // School telt mee als het die week als werk geldt (week A bij afwisselende regeling). Net als
      // kantoor: is de persoon die schooldag afwezig, dan gaan de uren naar schoolIneffUren i.p.v.
      // schoolUren. School is sowieso nooit effectief (geen straatdienst) — beide tellers landen
      // hieronder in ineffectiefUren.
      if (schoolTeltAlsWerk(mw, weekType, vakantieDagen)) {
        const uren = (mw.school.uren ?? 8) / deel;
        if (afwMap[mw.naam]?.has(mw.school?.dag)) schoolIneffUren += uren; // afwezig die dag -> ineffectief i.p.v.
        else schoolUren += uren;
      }
    }

    // ── Basis per actieve gemeente: kantoor/school is voor allemaal gelijk ──
    const gemeenten = {};
    for (const gemKey of actieveGemeenten) {
      gemeenten[gemKey] = {
        ingepland: 0, ineffectief: 0, effectief: 0,
        kantoorUren, schoolUren, kantoorIneffUren, schoolIneffUren, ineffectiefUren: 0, fte: 0,
        afwezigen: {},
      };
    }

    // ── Straatdiensten: per gemeenteKey uit de respons (namen + afwezigen) ──
    // `uitgevallenOpDag` is de brug naar de teamtelling verderop: het is de verzameling
    // (naam, dag) waarvoor geldt "stond die dag in een dienstcel én was afwezig" — precies
    // wat de "Uitgevallen"-regel onder een gemeenterij toont. De teamtelling gebruikt hem om
    // afwezige werkdagen te splitsen in wél-ingepland en niet-ingepland; zonder die splitsing
    // beweert het blok onder de tabel dat iedereen daar nergens ingepland stond, terwijl
    // dezelfde dag er bovenlangs al als ineffectieve dienst staat.
    // De dagcode komt uit dagObj (server/src/rooster-service.js:81-85), niet uit de dienst:
    // een dienst-object draagt zijn dag niet.
    // Alleen ACTIEVE gemeenten tellen mee — de `continue` hieronder slaat de andere over,
    // en dat is ook precies de verzameling waarover de "Uitgevallen"-regels gaan.
    const uitgevallenOpDag = new Set(); // `${naam}|${dag}`
    for (const dagObj of week.dagen || []) {
      for (const dienst of dagObj.diensten || []) {
        const gem = gemeenten[dienst.gemeenteKey];
        if (!gem) continue; // dienst van een niet-actieve/onbekende gemeente: overslaan
        gem.ingepland += (dienst.medewerkers || []).length;
        // Per naam TELLEN, niet alleen onthouden: iemand kan in dezelfde week meer dan één
        // dienst in dezelfde gemeente missen (Danny in wk32 Arnhem: di én vr). De kolom
        // Ineffectief telt diensten, dus zonder dat aantal telt de "Uitgevallen"-regel eronder
        // niet op tot datzelfde getal. Het TYPE blijft werken als voorheen: de laatste wint
        // bij een dubbele naam.
        for (const [naam, type] of Object.entries(dienst.afwezigen || {})) {
          gem.ineffectief += 1;
          gem.afwezigen[naam] = { type, aantal: (gem.afwezigen[naam]?.aantal ?? 0) + 1 };
          uitgevallenOpDag.add(`${naam}|${dagObj.dag}`);
        }
      }
    }

    // ── Afgeleiden per gemeente: effectief, ineffectiefUren (per gemeente!) + fte (1 decimaal) ──
    for (const gemKey of actieveGemeenten) {
      const g = gemeenten[gemKey];
      g.effectief = g.ingepland - g.ineffectief;
      // ALLE ineffectieve uren: uitgevallen straatdiensten (×8) + kantoor (bijgewoond + afwezig)
      // + school (bijgewoond + afwezig). Kantoor stond hier eerder alleen met kantoorIneffUren
      // in; de bijgewoonde kantooruren telden als GELEVERD mee in de fte-teller hieronder. Dat
      // is omgezet: kantoor is wel gewerkte tijd, maar geen straaturen.
      // GEEN DUBBELTELLING: kantoorUren en kantoorIneffUren sluiten elkaar uit — dezelfde
      // kantoordag landt in precies één van de twee (zie de if/else hierboven), en hetzelfde
      // geldt voor de twee school-tellers.
      g.ineffectiefUren = g.ineffectief * 8 + kantoorUren + kantoorIneffUren + schoolUren + schoolIneffUren;
      // Alleen STRAATDIENSTEN tellen als geleverd. Kantoor zat hier eerder bij.
      g.fte = Math.round((g.effectief * 8) / 36 * 10) / 10;
    }

    // ── Teamcapaciteit: contracturen van het hele team, en wat daarvan wegviel ──
    // Staat LOS van de gemeente-berekening hierboven: die telt roostercellen, dus wie al bij
    // het vastleggen verlof had is daar onzichtbaar (nooit ingepland). Deze telling vertrekt
    // juist vanuit het contract en trekt de afwezige would-be-werkdagen eraf.
    // Koppelen op mw.naam — dat is de sleutel die bouwAfwezigheidTypeMap teruggeeft, niet de mwKey.
    const afwTypeMap = bouwAfwezigheidTypeMap(afwezigheid, week.weeknummer, week.isoJaar);
    let capaciteitUren = 0;
    const teamAfwezigen = {};   // naam -> { type, uren } — uren per persoon, voor de regel onder de tabel
    for (const mw of roosterbareMw) {
      capaciteitUren += mw.contract_uren || 0;
      const afwDagen = afwTypeMap[mw.naam] || {};
      // wouldBeWerkdag is de POORT: alleen een dag waarop deze persoon structureel zou werken
      // telt als verloren dag. Een verlofdag in een OFF-weekend kost dus niets, want die dag
      // stond er toch al niet. Hij krijgt ALLE zes argumenten, net als de aanroep in
      // verwerkUren (rooster-engine.js) en roostercodes.js:409. `regels` is niet optioneel in de
      // praktijk: zonder die vijfde parameter vallen de vast_rotatiepatroon-weekenddagen weg
      // en tellen verlofdagen van die mensen niet mee. vakantieDagen (zesde) haalt de
      // vervallen schooldag er juist IN: die dag is in een vakantieweek een gewone werkdag.
      let afwezigeWerkdagen = 0;
      // Splitsing: stond deze persoon die dag in een dienstcel (uitgevallenOpDag) of niet?
      // Puur een WEERGAVE-splitsing — de poort en het totaal hieronder veranderen niet.
      let uitgevallenWerkdagen = 0;
      let type = null;          // eerste afwezige werkdag (ma→zo) bepaalt het label
      // ALLE typen die op de meegetelde dagen voorkwamen, in volgorde van eerste voorkomen.
      // `type` alleen is een onjuiste uitspraak zodra iemand in één week twee soorten
      // afwezigheid heeft: Jermaine was in wk32 dinsdag ZIEK en wo t/m za met VERLOF, en
      // kreeg daarmee voor al zijn vijf dagen het etiket "ziek". Per emmer apart bijgehouden,
      // want een emmer bevat maar een deel van de dagen.
      const typen = [];
      const typenUitgevallen = [];
      const typenNietIngepland = [];
      const onthoud = (lijst, t) => { if (!lijst.includes(t)) lijst.push(t); };
      for (const dag of ALLE_DAGEN) {
        if (!wouldBeWerkdag(mw, dag, weekType, week.weeknummer, regels, vakantieDagen)) continue;
        if (afwDagen[dag]) {
          afwezigeWerkdagen += 1;
          if (type === null) type = afwDagen[dag];
          onthoud(typen, afwDagen[dag]);
          if (uitgevallenOpDag.has(`${mw.naam}|${dag}`)) {
            uitgevallenWerkdagen += 1;
            onthoud(typenUitgevallen, afwDagen[dag]);
          } else {
            onthoud(typenNietIngepland, afwDagen[dag]);
          }
        }
      }
      // Verlof én ziekte tellen allebei als niet inzetbaar; het type is alleen voor het label.
      //
      // EEN AFWEZIGE WERKDAG KOST 8 UUR — een dienst. Stond eerder als
      // contract_uren × (afwezigeWerkdagen / werkdagen): dat verdeelde het contract over de
      // BESCHIKBARE dagen, waardoor dezelfde verlofdag 4,6u waard was in een week met zeven
      // beschikbare dagen (Emre, wk37) en 8,0u in een week met vier (Emre, wk38). Naast de
      // kolom Ineffectief, die diensten × 8 telt, stonden zo twee definities van dezelfde
      // verloren dag in één overzicht.
      //
      // De poort hierboven (wouldBeWerkdag, r.189) blijft ongewijzigd: alleen dagen waarop
      // iemand structureel zou werken tellen mee, dus een verlofdag in een OFF-weekend telt
      // nog steeds niet.
      //
      // AFTOPPING OP HET WEEKCONTRACT. Zonder plafond kan iemand meer niet-inzetbare uren
      // krijgen dan er is ingekocht: Emre is in een ON-weekend zeven dagen beschikbaar, dus
      // 7 × 8 = 56u tegenover een contract van 32u. Meer uitval dan contract bestaat niet.
      const persoonsUren = Math.min(afwezigeWerkdagen * 8, mw.contract_uren || 0);
      if (afwezigeWerkdagen > 0) {
        const uren = Math.round(persoonsUren * 10) / 10;
        // NAAR RATO VERDELEN, en NIET de twee emmers los aftoppen. De aftopping hierboven
        // geldt op het WEEKTOTAAL: zou je Math.min per emmer herhalen, dan kan de som van de
        // twee emmers boven het contract uitkomen (Alper, 5 afwezige dagen op een contract van
        // 36u: 40u wordt 36u — twee losse aftoppingen zouden daar 36 + 36 van maken). Daarom
        // eerst het totaal precies zoals voorheen, en dat totaal daarna splitsen.
        // De rest-berekening (niet nog een keer ronden op de deling) garandeert dat de twee
        // delen exact optellen tot `uren`, ook als de deling niet mooi uitkomt.
        const urenUitgevallen = Math.round(uren * (uitgevallenWerkdagen / afwezigeWerkdagen) * 10) / 10;
        const urenNietIngepland = Math.round((uren - urenUitgevallen) * 10) / 10;
        teamAfwezigen[mw.naam] = {
          type,                 // ongewijzigd: eerste afwezige werkdag — nog gelezen door test/diag
          typen,
          uren,                 // ongewijzigd totaal: urenUitgevallen + urenNietIngepland
          urenUitgevallen,
          typenUitgevallen,
          urenNietIngepland,
          typenNietIngepland,
        };
      }
    }
    // De buitenste afronding vangt de drijvendekomma-ruis van het optellen van tienden
    // (0.1 + 0.2 = 0.30000000000000004); de som blijft gelijk aan wat er per naam staat.
    const nietInzetbaarUren = Math.round(
      Object.values(teamAfwezigen).reduce((s, a) => s + a.uren, 0) * 10,
    ) / 10;
    // De twee subtotalen onder de tabel. `uitgevallenUren` is de som van de emmers; het
    // niet-ingeplande deel is bewust het RESTANT en niet een tweede som, zodat de drie
    // getallen op het scherm exact sluiten (zelfde truc als inzetbaarUren hieronder).
    const uitgevallenUren = Math.round(
      Object.values(teamAfwezigen).reduce((s, a) => s + a.urenUitgevallen, 0) * 10,
    ) / 10;
    const nietIngeplandUren = Math.round((nietInzetbaarUren - uitgevallenUren) * 10) / 10;
    const team = {
      aantal: roosterbareMw.length,
      capaciteitUren,
      nietInzetbaarUren,
      // Splitsing van nietInzetbaarUren; samen exact nietInzetbaarUren.
      nietIngeplandUren,
      uitgevallenUren,
      // Bewust van de AFGERONDE niet-inzetbaar afgetrokken, zodat de drie getallen op het
      // scherm exact optellen (capaciteit − niet inzetbaar == inzetbaar).
      inzetbaarUren: Math.round((capaciteitUren - nietInzetbaarUren) * 10) / 10,
      afwezigen: teamAfwezigen,
    };

    return { weeknummer: week.weeknummer, isoJaar: week.isoJaar, gemeenten, team };
  });
}
